import { TRIAL_BOOKING_STATE } from '@common/enums'
import AlertMessage from '@components/atoms/alert-message'
import MsgTrans from '@components/common/i18n/message-trans'
import Spinner from '@components/common/spinner'
import TimezoneModal from '@components/trial-class/timezone-modal'
import useResolution from '@hooks/useResolution'
import useSegment, { SegmentEventEnum } from '@hooks/useSegment'
import { BOOKING_PAGE_HEADING } from '@lib/enums/booking'
import date, { formatTimeByTimezone } from '@lib/utils/date.utils'
import { trackTrialSlotViewed } from '@lib/utils/trial-class'
import { t, Trans } from '@lingui/macro'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { i18n } from '@lingui/core'
import Modal from '@components/common/modal'
import { MixpanelEvent, MixpanelProperties } from '@lib/analytics/mixpanel'

type Props = {
  slots: any
  selectedDate: string
  setSelectedDate: React.Dispatch<React.SetStateAction<string>>
  selectedTime: string
  setSelectedTime: React.Dispatch<React.SetStateAction<string>>
  onBook: () => void
  isBooking: boolean
  selectedTimezone: string
  setSelectedTimezone?: React.Dispatch<React.SetStateAction<string>>
  isInstantClassAvailable?: boolean
  setSelectSlotAllowed?: React.Dispatch<React.SetStateAction<boolean>>
  findInstantClass?: string
  title?: string
  timezoneAlertBgColor?: 'blue' | 'yellow'
  showLimitedSlotsAlert?: boolean
  showBookingStats?: boolean
  ctaText?: string
  setNumDaysToShow?: React.Dispatch<React.SetStateAction<number>>
  setIsShowingMoreSlots?: React.Dispatch<React.SetStateAction<boolean>>
  isShowingMoreSlots?: boolean
  isShowMoreSlotsOption?: boolean
  onGoBack?: () => void
  error?: string
  segmentTrackingEvents?: {
    clickDate: SegmentEventEnum
    clickTime: SegmentEventEnum
  }

  isInteachertrialClassroom?: boolean
  studentName?: string
  studentEmail?: string
  studentGrade?: string
  trialSubject?: string
  // Mixpanel tracking. Each screen passes the event names it wants for the
  // actions below; actions without a name are not tracked.
  trackMixpanel?: (
    event: MixpanelEvent,
    properties?: MixpanelProperties
  ) => void
  mixpanelEvents?: AvailableSlotsMixpanelEvents
}

export type AvailableSlotsMixpanelEvents = Partial<
  Record<
    | 'clickDate'
    | 'clickTime'
    | 'clickBookNow'
    | 'clickChangeGrade'
    | 'clickChangeTimezone'
    | 'clickMoreSlotOptions'
    | 'confirmationModalVisit'
    | 'confirmationModalClickChooseAnotherTime'
    | 'confirmationModalPressEscape'
    | 'confirmationModalClose',
    MixpanelEvent
  >
>

const LIMITED_SLOTS_THRESHOLD = 10
const MOBILE_SLOT_ROW_COUNT = 5.25
const DESKTOP_SLOT_ROW_COUNT = 3.25
const MOBILE_SLOT_ROW_HEIGHT = 47
const DESKTOP_SLOT_ROW_HEIGHT = 50

const AvailableSlots = ({
  slots,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  onBook,
  isBooking,
  findInstantClass,
  selectedTimezone,
  setSelectedTimezone,
  title,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  timezoneAlertBgColor = 'blue',
  showLimitedSlotsAlert = false,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showBookingStats = false,
  ctaText = t({
    id: 'trialClass.booking.ctaText',
    message: `Book my FREE class`,
  }),
  onGoBack,
  error = '',
  segmentTrackingEvents,
  isInteachertrialClassroom,
  studentName,
  studentEmail,
  studentGrade,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  trialSubject = 'Coding',
  setNumDaysToShow,
  setIsShowingMoreSlots,
  isShowingMoreSlots,
  isShowMoreSlotsOption,
  trackMixpanel,
  mixpanelEvents = {},
}: Props) => {
  const track = useSegment()
  const [slotsToDisplay, setSlotsToDisplay] = useState([])
  const [isTimezoneModalOpen, setIsTimezoneModalOpen] = useState(false)
  const router = useRouter()
  const { course } = router.query
  const [showBookingProgress, setShowBookingProgress] = useState(false)
  const [progress, setProgress] = useState(0)
  const [remainingSeconds, setRemainingSeconds] = useState(3)
  const sanitizedCourse =
    typeof course === 'string' ? course.replace(/-/g, '') : ''

  const trackSlotsEvent = (action: keyof AvailableSlotsMixpanelEvents) => {
    const event = mixpanelEvents[action]
    if (event) trackMixpanel?.(event)
  }

  const handleCancelBooking = () => {
    setShowBookingProgress(false)
    setProgress(0)
    setRemainingSeconds(5)
  }

  const {
    sizes: { sm },
  } = useResolution()
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        trackTrialSlotViewed(course)
      } catch (error) {
        console.error('Error tracking trial slot view:', error)
      }
    }
  }, [])

  useEffect(() => {
    setSlotsToDisplay(slots[selectedDate])
  }, [selectedDate])

  // Auto-booking progress timer with 3-second countdown
  useEffect(() => {
    if (!showBookingProgress) {
      return
    }

    const totalDuration = 3000 // 3 seconds
    const intervalMs = 50 // Update every 50ms for smooth animation
    const steps = totalDuration / intervalMs
    const progressIncrement = 100 / steps
    const secondsDecrement = 3 / steps

    let currentProgress = 0
    let currentSeconds = 3

    const timer = setInterval(() => {
      currentProgress += progressIncrement
      currentSeconds -= secondsDecrement

      if (currentProgress >= 100) {
        setProgress(100)
        setRemainingSeconds(0)
        clearInterval(timer)
        // Auto-submit booking
        onBook()
        setShowBookingProgress(false)
        setProgress(0)
        setRemainingSeconds(3)
      } else {
        setProgress(currentProgress)
        setRemainingSeconds(Math.ceil(currentSeconds))
      }
    }, intervalMs)

    return () => clearInterval(timer)
  }, [showBookingProgress, onBook])

  // Keyboard handler for Escape key
  useEffect(() => {
    if (!showBookingProgress) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        trackSlotsEvent('confirmationModalPressEscape')
        handleCancelBooking()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [showBookingProgress])

  return (
    <div data-testid="available-slots" className="flex flex-col">
      {title ? (
        <h4 className="text-xl leading-tight text-grey font-700">{title}</h4>
      ) : (
        <>
          {findInstantClass === TRIAL_BOOKING_STATE.FAIL ? (
            // TODO: Remove condition
            <div className="text-3xl text-grey font-600">
              <div>Sorry! No live teachers available now.</div>
              <div>Please book a convenient slot</div>
            </div>
          ) : (
            <>
              <div className="mb-4 lg:mt-6">
                <h4 className="text-3xl leading-tight text-grey font-600 mb-3">
                  <MsgTrans
                    msg={
                      course
                        ? BOOKING_PAGE_HEADING[`${sanitizedCourse}`]
                        : BOOKING_PAGE_HEADING.coding
                    }
                  />
                </h4>
                {!isInteachertrialClassroom && studentGrade && (
                  <div className="text-base text-grey-800 flex flex-wrap items-center font-medium">
                    <div
                      title="Ensure your grade is correct so that right teacher can be assigned"
                      className="flex items-center"
                    >
                      <Trans>
                        <span>
                          You&apos;re in{' '}
                          <span className="font-600">{studentGrade}</span>
                        </span>
                      </Trans>
                      <button
                        className="ml-1 text-blue-200 text-sm underline cursor-pointer hover:text-orange focus:outline-none"
                        onClick={() => {
                          trackSlotsEvent('clickChangeGrade')
                          onGoBack && onGoBack()
                        }}
                        title="Change your grade to get matched with the right teacher"
                      >
                        <Trans>Change</Trans>
                      </button>
                    </div>
                    <span className="mx-2">•</span>
                    <span>
                      Local time:{' '}
                      <span className="font-600">
                        {date().tz(selectedTimezone).format('hh:mm A')}
                      </span>
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {isInteachertrialClassroom && (
        <div className="flex flex-col mt-2 bg-grey-200 rounded p-5 py-3">
          <p>
            Name: <span className="font-600 text-orange">{studentName}</span>
          </p>
          <p>
            Email: <span className="font-600 text-orange">{studentEmail}</span>
          </p>
          <p>
            Grade: <span className="font-600 text-orange">{studentGrade}</span>
          </p>
        </div>
      )}

      <div className="flex flex-col mt-3">
        <span className="font-600 text-grey text-xl">
          <Trans id="trialClass.booking.selectDate">Select a date</Trans>
        </span>
        <div className="grid grid-cols-2 mt-2 lg:grid-cols-3 gap-x-3 gap-y-3">
          {Object.keys?.(slots)?.map((slot) => (
            <button
              key={slot}
              type="button"
              onClick={() => {
                trackSlotsEvent('clickDate')
                segmentTrackingEvents?.clickDate &&
                  track(segmentTrackingEvents?.clickDate)
                setSelectedDate(slot)
                setSelectedTime(null)
              }}
              data-testid={slot}
              className={`font-600 focus:outline-none border border-grey-500 rounded-lg py-2 px-2 lg:px-4 hover:border-blue-200 text-sm lg:text-base ${
                selectedDate === slot ? 'bg-blue-200 text-white' : 'text-grey'
              }`}
            >
              {i18n.date(date(slot, 'DD-MM-YYYY').toDate(), {
                day: '2-digit',
                month: 'short',
                weekday: 'short',
              })}
            </button>
          ))}
        </div>

        <div className="mt-5 flex flex-wrap items-center">
          <span className="text-grey font-600 text-xl">
            <Trans id="trialClass.booking.selectTime">Select a time</Trans>
          </span>
          <div className="ml-2 text-base text-grey-800 flex items-center font-medium">
            ({selectedTimezone}
            <button
              className="ml-1 text-blue-200 text-sm underline cursor-pointer hover:text-orange focus:outline-none"
              onClick={() => {
                trackSlotsEvent('clickChangeTimezone')
                setIsTimezoneModalOpen(true)
              }}
            >
              <Trans>Change</Trans>
            </button>
            )
          </div>
        </div>
        <div
          className="flex flex-wrap mt-2 overflow-auto lg:grid-cols-5 thin-scroll-bar"
          style={{
            maxHeight: `${
              sm
                ? MOBILE_SLOT_ROW_HEIGHT * MOBILE_SLOT_ROW_COUNT
                : DESKTOP_SLOT_ROW_HEIGHT * DESKTOP_SLOT_ROW_COUNT
            }px`,
          }}
        >
          {slotsToDisplay.map((slot) => (
            <div key={slot} className="flex">
              <button
                type="button"
                data-testid="time-slot-button"
                onClick={() => {
                  trackSlotsEvent('clickTime')
                  setSelectedTime(slot)
                  segmentTrackingEvents?.clickTime &&
                    track(segmentTrackingEvents.clickTime)
                }}
                className={`font-600 focus:outline-none border border-grey-500 w-32 rounded-lg mr-2 mb-2 py-2 px-2 lg:px-4 text-sm lg:text-base hover:border-blue-200 ${
                  selectedTime === slot ? 'bg-blue-200 text-white' : 'bg-white'
                }`}
              >
                {formatTimeByTimezone(
                  date(slot).toDate(),
                  selectedTimezone,
                  'hh:mm A'
                )}
              </button>
            </div>
          ))}
        </div>
      </div>
      {error && <AlertMessage className="self-start">{error}</AlertMessage>}

      {showLimitedSlotsAlert &&
        selectedDate === date().tz(selectedTimezone).format('DD-MM-YYYY') &&
        slotsToDisplay.length <= LIMITED_SLOTS_THRESHOLD && (
          <AlertMessage type="info" className="self-start">
            <Trans id="trialClass.booking.onlyOneSlotLeftAlert">
              Only {slotsToDisplay.length}{' '}
              {slotsToDisplay.length > 1 ? 'slots' : 'slot'} left for today,
              hurry up!
            </Trans>
          </AlertMessage>
        )}
      <div className="flex items-center mt-3">
        <button
          className={`btn btn-orange py-4 px-8 flex justify-center rounded-lg text-lg font-600 ${
            isBooking ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
          }`}
          data-testid="book-trial-class-btn"
          onClick={() => {
            trackSlotsEvent('clickBookNow')
            if (!selectedDate || !selectedTime) {
              // Don't show modal if date/time not selected - onBook will show error
              onBook()
              return
            }
            trackSlotsEvent('confirmationModalVisit')
            setShowBookingProgress(true)
            track(SegmentEventEnum.TRIAL_CLASS_BOOKING_FLOW_CLICK_BOOK_NOW)
          }}
          disabled={isBooking}
        >
          {isBooking ? <Spinner classes="h-5 w-5 text-white" /> : ctaText}
        </button>
      </div>
      {!isShowingMoreSlots && isShowMoreSlotsOption && (
        <button
          className="font-600 text-lg hover:underline text-orange active:outline-none focus:outline-none mt-4 mb-2 w-56 text-left hover:text-orange-700"
          onClick={() => {
            trackSlotsEvent('clickMoreSlotOptions')
            setNumDaysToShow(7)
            setIsShowingMoreSlots(true)
          }}
        >
          <Trans>I need more slot options</Trans>
        </button>
      )}

      <TimezoneModal
        isOpen={isTimezoneModalOpen}
        setIsOpen={setIsTimezoneModalOpen}
        selectedTimezone={selectedTimezone}
        setSelectedTimezone={setSelectedTimezone}
      />

      <Modal
        isOpen={showBookingProgress}
        setIsOpen={(open: boolean) => {
          if (!open) trackSlotsEvent('confirmationModalClose')
          setShowBookingProgress(open)
        }}
        shouldCloseOnEsc={false}
      >
        <div className="text-center">
          {/* Animated booking icon with pulse effect */}
          <div className="flex justify-center mb-6">
            <div
              className="relative"
              role="status"
              aria-live="polite"
              aria-label={`Booking in progress. ${remainingSeconds} seconds remaining`}
            >
              <div className="absolute inset-0 bg-orange rounded-full opacity-25 animate-ping"></div>
              <div className="relative w-16 h-16 bg-orange rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Heading */}
          <h3 className="text-2xl font-600 text-grey-900 mb-2">
            <Trans id="trialClass.booking.progressModal.heading">
              Booking your free trial now...
            </Trans>
          </h3>

          {/* Countdown display */}
          <p className="text-lg text-grey-800 mb-4">
            <Trans id="trialClass.booking.progressModal.countdown">
              Booking in {remainingSeconds} second
              {remainingSeconds !== 1 ? 's' : ''}
            </Trans>
          </p>

          {/* Selected date and time display */}
          <div className="mb-6 p-4 bg-grey-200 rounded-lg">
            <div className="text-sm text-grey-700 mb-1">
              <Trans>Selected slot:</Trans>
            </div>
            <div className="font-600 text-base text-grey-900">
              {selectedDate && selectedTime && (
                <>
                  {i18n.date(date(selectedDate, 'DD-MM-YYYY').toDate(), {
                    day: '2-digit',
                    month: 'short',
                    weekday: 'short',
                  })}{' '}
                  at{' '}
                  {formatTimeByTimezone(
                    date(selectedTime).toDate(),
                    selectedTimezone,
                    'hh:mm A'
                  )}
                </>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="w-full bg-grey-400 rounded-full h-2 overflow-hidden">
              <div
                className="bg-orange h-2 rounded-full transition-all duration-75 ease-linear"
                style={{ width: `${progress}%` }}
                role="progressbar"
                aria-valuenow={Math.round(progress)}
                aria-valuemin={0}
                aria-valuemax={100}
              ></div>
            </div>
          </div>

          {/* Cancel link */}
          <button
            className="text-blue-200 text-sm hover:underline focus:outline-none mt-4"
            onClick={() => {
              trackSlotsEvent('confirmationModalClickChooseAnotherTime')
              handleCancelBooking()
            }}
          >
            <Trans id="trialClass.booking.progressModal.cancel">
              Choose another time
            </Trans>
          </button>

          {/* Subtle reassurance text */}
          <p className="text-sm text-grey-800 mt-4">
            <Trans id="trialClass.booking.progressModal.reassurance">
              Your instructor will be notified once booking completes
            </Trans>
          </p>
        </div>
      </Modal>
    </div>
  )
}

export default AvailableSlots
